import { useCallback, useEffect, useState } from "react";
import {
  Command,
  CommandEmpty,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command.tsx";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover.tsx";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select.tsx";
import { cn } from "@/lib/utils.ts";
import { search_bizminer_admin_industries_path } from "@/routes";

export interface IndustrySearchResult {
  id: string;
  name: string;
  code: string;
  level: number;
  ancestor_ids: string[];
}

interface Props {
  onSelect: (result: IndustrySearchResult) => void;
}

const LEVEL_LABELS: Record<number, string> = {
  1: "Sector",
  2: "Industry Group",
  3: "Industry",
  4: "Sub-Industry",
};

const LEVEL_COLORS: Record<number, string> = {
  1: "bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300",
  2: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300",
  3: "bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-300",
  4: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300",
};

export default function IndustriesSearch({ onSelect }: Props) {
  const [query, setQuery] = useState("");
  const [level, setLevel] = useState<string>("all");
  const [results, setResults] = useState<IndustrySearchResult[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const search = useCallback(async (q: string, lvl: string) => {
    if (q.length < 2) {
      setResults([]);
      return;
    }

    setIsLoading(true);
    try {
      const params = new URLSearchParams({ q });
      if (lvl !== "all") params.append("level", lvl);

      const response = await fetch(
        `${search_bizminer_admin_industries_path()}?${params}`,
      );
      const data: IndustrySearchResult[] = await response.json();
      setResults(data);
    } catch (error) {
      console.error("Search failed:", error);
      setResults([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      search(query, level);
    }, 300);

    return () => clearTimeout(timer);
  }, [query, level, search]);

  const handleSelect = (result: IndustrySearchResult) => {
    onSelect(result);
    setQuery("");
    setResults([]);
    setIsOpen(false);
  };

  return (
    <div className="flex items-center gap-2">
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            className="h-9 w-64 px-3 rounded-md border border-input bg-background text-sm text-left text-muted-foreground hover:bg-accent hover:text-accent-foreground focus:outline-none focus:ring-2 focus:ring-ring"
          >
            Search industries...
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-96 p-0" align="end">
          <Command shouldFilter={false}>
            <CommandInput
              value={query}
              onValueChange={setQuery}
              placeholder="Search industries..."
            />
            <CommandList>
              {isLoading && (
                <div className="py-6 text-center text-sm text-muted-foreground">
                  Searching...
                </div>
              )}
              {!isLoading && query.length >= 2 && results.length === 0 && (
                <CommandEmpty>No results found</CommandEmpty>
              )}
              {!isLoading &&
                results.map((result) => (
                  <CommandItem
                    key={result.id}
                    value={result.id}
                    onSelect={() => handleSelect(result)}
                    className="flex items-start gap-2"
                  >
                    <span
                      className={cn(
                        "shrink-0 px-1.5 py-0.5 rounded text-xs font-medium",
                        LEVEL_COLORS[result.level],
                      )}
                    >
                      L{result.level}
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="font-medium text-sm truncate">
                        {result.name}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {result.code} · {LEVEL_LABELS[result.level]}
                      </div>
                    </div>
                  </CommandItem>
                ))}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      <Select value={level} onValueChange={setLevel}>
        <SelectTrigger className="h-9 w-[160px]">
          <SelectValue placeholder="All levels" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All levels</SelectItem>
          <SelectItem value="1">L1 - Sector</SelectItem>
          <SelectItem value="2">L2 - Industry Group</SelectItem>
          <SelectItem value="3">L3 - Industry</SelectItem>
          <SelectItem value="4">L4 - Sub-Industry</SelectItem>
        </SelectContent>
      </Select>
    </div>
  );
}
